/**
 * MZRTPlotLegendStrip — MZRT 플롯 아래 **한 줄** 범례.
 *
 * P2-3 DOM 안정화: MZRTPlot의 ScatterChart 자체와 분리하여 별도 memo 컴포넌트로 격리.
 * selectedFeature 변경 시 ScatterChart 리렌더 없이 이 컴포넌트만 업데이트됨.
 *
 * B-4: selectedFeature를 prop drilling 대신 useAnalysisStore slice로 직접 구독.
 * MZRTPlot 자체는 selectedFeatureId 변경에 무관 — data + features prop만 의존.
 */
import { memo } from "react";
import InfoDot from "@/components/charts/InfoDot";
import { useAnalysisStore } from "@/stores/useAnalysisStore";
import type { FeatureData } from "@/types/spectrum";
import {
  IDENTIFIED_RAMP_FROM,
  IDENTIFIED_RAMP_TO,
  MEASURED_ONLY_COLOR,
} from "@/utils/featureMapping";
import { findObservationOf } from "@/utils/observationGrouping";

interface MZRTPlotLegendStripProps {
  features: FeatureData[];
  /** 화면에 점이 있는 동정 관찰 수. */
  shownObservationCount: number;
  /** 이 분석의 전체 동정 관찰 수. */
  totalObservationCount: number;
  /**
   * 화면에 실제로 점이 있는 관찰 id. 브러시가 걸리면 그 구간의 것만 담긴다.
   * 미지정이면 범위 검사를 하지 않는다.
   */
  visibleFeatureIds?: ReadonlySet<string>;
}

export const IDENTIFIED_KEY_LABEL = "동정된 관찰";
export const MEASURED_ONLY_KEY_LABEL = "측정만 된 스캔";
/**
 * 강도 램프 제목 — 눈금(0 / 100%)과 함께 쓴다.
 *
 * 2026-09-06 (운영자): "원래 % 표시되는건 더이상 필요 없는 부분인가? …
 * 우리는 전문 SW니깐 세련되게 표현될수록 좋아요."
 *
 * 되살린다. 색띠만 두면 "진할수록 강하다"는 방향만 알 뿐 **눈금이 어디에
 * 걸려 있는지**를 모른다. MS 에서 기준피크 대비 100% 는 표준 관용이므로
 * 양 끝을 밝히는 편이 오히려 전문적이다. 다만 문장으로 늘어놓지 않고
 * 색띠 양옆의 눈금으로만 둔다 — 분모의 한계는 ⓘ 가 맡는다.
 */
export const INTENSITY_RAMP_LABEL = "스캔 내 상대강도";

/**
 * 범례가 다 말하지 못하는 단서 — ⓘ 툴팁으로 뺀다.
 *
 * 2026-09-06 (운영자): "하단 스펙트럼 주위의 쓸데없는 말이 많아. 핵심만
 * 간결하게." 화면에 상시 필요한 것은 **색 키**뿐이다(색이 두 부류를 가르는
 * 유일한 채널이므로). 분모의 범위·데시메이션 규칙·클릭 결과는 사실이지만
 * 매 순간 읽을 것은 아니다 — 지우지 않고 여기로 옮긴다.
 */
export const LEGEND_DETAIL_NOTE = [
  "색 — 보라: AI 후보가 기록된 스캔(누르면 후보가 선택됩니다) / 회색: 측정만 된 스캔(누르면 그 스캔의 스펙트럼만 봅니다).",
  "진하기 — 그 점이 속한 스캔 안에서의 상대강도이고 각 스캔의 최대 이온이 100%입니다. 관찰마다 분모가 달라 다른 관찰끼리는 색을 비교할 수 없습니다.",
  "점 — 표시 상한이 있어 스캔마다 신호가 큰 m/z 부터 그립니다. 측정된 이온 전부가 아닙니다.",
].join("\n");

/** 색 견본 — 점과 같은 규칙으로 칠한다. */
function Swatch({ color }: { color: string }) {
  return (
    <span
      className="h-2.5 w-2.5 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}

function MZRTPlotLegendStrip({
  features,
  shownObservationCount,
  totalObservationCount,
  visibleFeatureIds,
}: MZRTPlotLegendStripProps) {
  const selectedFeature = useAnalysisStore((s) => s.selectedFeatureId);
  // T1: 점 색상은 관찰(스캔)의 대표(1순위) 후보 F-번호를 따른다 — 형제 후보를
  // 골라도 같은 스캔이므로 같은 점이다.
  const observation = findObservationOf(features, selectedFeature);
  const activeFeatureId =
    observation?.features[0]?.feature_id ?? selectedFeature;

  // 브러시가 선택 관찰을 화면 밖으로 밀어냈는가. 선택은 해제되지 않으므로
  // (handleTICZoom 은 brushRange 만 바꾼다) 여기서 확인하지 않으면 화면에
  // 없는 점을 계속 지목하게 된다.
  const selectionOffScreen =
    activeFeatureId != null &&
    visibleFeatureIds != null &&
    !visibleFeatureIds.has(activeFeatureId);

  /**
   * 동정 관찰 수 — 색 키의 라벨이 곧 개수다.
   *
   * 완전성 주장은 **관찰에만** 붙인다. 점은 백엔드가 스캔당 상위 K개만
   * 내려보내므로(samples.py `_downsample_mzrt_points`) "점을 전부 표시한다"고
   * 말할 수 없고, 반대로 상한 이하이면 데시메이션을 아예 하지 않는데 응답에
   * 그 여부가 없다. 라벨된 스캔은 예산이 아무리 쪼그라들어도 최소 1점이
   * 남으므로 관찰 수만은 안전하게 단언할 수 있다.
   */
  const identifiedLabel =
    shownObservationCount === 0
      ? totalObservationCount > 0
        ? `${IDENTIFIED_KEY_LABEL} 없음 (전체 ${totalObservationCount}건)`
        : `${IDENTIFIED_KEY_LABEL} 없음`
      : shownObservationCount === totalObservationCount
        ? `${IDENTIFIED_KEY_LABEL} ${shownObservationCount}건`
        : `${IDENTIFIED_KEY_LABEL} ${shownObservationCount}건 (전체 ${totalObservationCount}건)`;

  /**
   * 선택 안내 — 사이드바가 이미 말하는 것은 되풀이하지 않는다.
   *
   * 오른쪽 "AI 분석 요약" 이 선택 물질·RT·m/z 를 이미 보여주므로, 여기서는
   * 그것이 말하지 않는 두 가지만 말한다: ① 고른 후보가 화면 밖일 때
   * ② 같은 스캔에 형제 후보가 있어 점이 대표 F-번호의 것일 때.
   */
  const selectionNote = !selectedFeature
    ? null
    : selectionOffScreen
      ? `${selectedFeature} 는 지금 보고 있는 구간 밖입니다`
      : observation && observation.size > 1
        ? `같은 스캔 후보 ${observation.size}개 · 점은 대표 ${activeFeatureId} 의 것`
        : null;

  return (
    <div className="mt-2 flex flex-col items-center gap-1">
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-gray-500">
        <span className="flex items-center gap-1.5">
          <Swatch color={IDENTIFIED_RAMP_TO} />
          {identifiedLabel}
        </span>
        <span className="flex items-center gap-1.5">
          <Swatch color={MEASURED_ONLY_COLOR} />
          {MEASURED_ONLY_KEY_LABEL}
        </span>
        <span className="flex items-center gap-1.5">
          {INTENSITY_RAMP_LABEL}
          <span className="tabular-nums text-gray-400">0</span>
          <span
            className="h-2.5 w-14 rounded-full"
            style={{
              background: `linear-gradient(to right, ${IDENTIFIED_RAMP_FROM}, ${IDENTIFIED_RAMP_TO})`,
            }}
          />
          <span className="tabular-nums text-gray-400">100%</span>
        </span>
        <InfoDot note={LEGEND_DETAIL_NOTE} />
      </div>

      {/* 자리를 **항상** 잡는다. 조건부로 붙이면 점을 누를 때마다 이 줄이
          생겼다 없어져 아래 TIC·질량스펙트럼 패널이 20px 씩 밀린다(실측:
          MZRT 패널 476 ↔ 496px). 증거 화면에서 클릭할 때마다 레이아웃이
          튀면 안 된다 — 운영자 지적 2026-09-06. */}
      <p className="min-h-4 text-[11px] text-gray-400">{selectionNote}</p>
    </div>
  );
}

export default memo(MZRTPlotLegendStrip);
